//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback, useRef, useState } from 'react';
import {
  Alert, AlertColor, Box, CircularProgress, Snackbar, Typography
} from '@mui/material';
import { SnackbarContext, type StatusEntry } from '@/contexts/SnackbarContext';

interface SnackbarProviderProps {
  children: React.ReactNode;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

// Circular progress with the percent in the middle; indeterminate when no
// percent is known yet.
function CircularWithLabel({ percent }: { percent?: number | null }) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {/* Decorative: the percent in the middle and the status text beside
          the ring already say what is running and how far along it is. */}
      <CircularProgress
        aria-hidden
        size={38}
        variant={percent == null ? 'indeterminate' : 'determinate'}
        value={percent ?? 0} />
      {percent != null && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex',
                   alignItems: 'center', justifyContent: 'center' }}>
          <Typography component="span" variant="caption"
            sx={{ fontSize: '0.6rem', lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums' }}>
            {`${Math.round(percent)}%`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false,
                                                            message: '',
                                                            severity: 'info' });
  const [statuses, setStatuses] = useState<Record<string, StatusEntry>>({});
  // Keys the user dismissed with the banner's close button.  Polling keeps
  // pushing updates, so a dismissed key stays hidden until its task finishes
  // (setStatus(key, null)), which clears the dismissal for the next run.
  const dismissed = useRef<Set<string>>(new Set());

  const showSnackbar = useCallback(
    (message: string, severity: AlertColor = 'info') => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const closeSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Keep the state updaters pure: the dismissed-set reads/mutations happen
  // outside setStatuses so they don't run twice under StrictMode / concurrent
  // rendering (which double-invokes updaters).
  const setStatus = useCallback((key: string, status: StatusEntry | null) => {
    if (status == null) {
      dismissed.current.delete(key);
      setStatuses(prev => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      if (dismissed.current.has(key)) return;
      setStatuses(prev => ({ ...prev, [key]: status }));
    }
  }, []);

  // Dismiss the whole status banner; remembers the keys so polling doesn't
  // immediately bring them back.
  const closeStatuses = useCallback(() => {
    Object.keys(statuses).forEach(key => dismissed.current.add(key));
    setStatuses({});
  }, [statuses]);

  const hasStatus = Object.keys(statuses).length > 0;

  return (
    <SnackbarContext.Provider value={{ showSnackbar, setStatus }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // When the persistent status banner is up, stack this transient one
        // above it instead of overlapping.
        sx={hasStatus ? { bottom: { xs: 90 } } : undefined}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={hasStatus}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          icon={false}
          onClose={closeStatuses}
          // Size to content and keep each row on one line, so the banner is as
          // wide as it needs to be (no wasted space, no wrapping).  Center the
          // close (X) vertically -- MUI aligns the action to the top by default.
          sx={{
            maxWidth: '92vw',
            alignItems: 'center',
            '& .MuiAlert-action': { alignItems: 'center', p: 0, mr: 0 }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1,
                     // Center a lone line; left-align when stacked so the rows
                     // line up rather than looking ragged.
                     alignItems: Object.keys(statuses).length > 1 ?
                                 'flex-start' : 'center',
                     fontVariantNumeric: 'tabular-nums' }}>
            {Object.entries(statuses).map(([key, status]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center',
                                   gap: 1.5 }}>
                <CircularWithLabel percent={status.percent} />
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {status.text}
                </Box>
              </Box>
            ))}
          </Box>
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
